import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/lib/api';
import { clearSupabaseSessionStorage, hasSupabaseConfig, supabase } from '@/lib/supabaseClient';

const AuthContext = createContext(null);
const SUPABASE_REQUIRED_MESSAGE = 'Supabase Auth is niet geconfigureerd voor deze omgeving.';
const MAX_REASONABLE_JWT_LENGTH = 4096;

function looksLikeJwt(token) {
  if (typeof token !== 'string') {
    return false;
  }

  const normalized = token.trim();
  if (!normalized || normalized.length > MAX_REASONABLE_JWT_LENGTH) {
    return false;
  }

  return normalized.split('.').length === 3;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const syncRef = useRef({ token: null, promise: null });
  const tokenRef = useRef(null);
  const token = session?.access_token || null;

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const clearAuthState = useCallback(() => {
    syncRef.current = { token: null, promise: null };
    tokenRef.current = null;
    clearSupabaseSessionStorage();
    setSession(null);
    setUser(null);
  }, []);

  const loadUserFromToken = useCallback(async (accessToken) => {
    const res = await axios.get(`${API_BASE_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return res.data;
  }, []);

  const ensureSupabaseReady = useCallback(() => {
    if (!hasSupabaseConfig || !supabase) {
      throw new Error(SUPABASE_REQUIRED_MESSAGE);
    }
  }, []);

  const syncSession = useCallback(async (nextSession, options = {}) => {
    const { clearOnEmpty = true, showLoading = true } = options;
    const accessToken = nextSession?.access_token || null;
    if (showLoading) {
      setLoading(true);
    }

    if (!accessToken) {
      if (clearOnEmpty) {
        clearAuthState();
      }
      if (showLoading) {
        setLoading(false);
      }
      return null;
    }

    if (!looksLikeJwt(accessToken)) {
      clearAuthState();
      if (showLoading) {
        setLoading(false);
      }
      return null;
    }

    setSession(nextSession);
    tokenRef.current = accessToken;

    if (syncRef.current.token === accessToken && syncRef.current.promise) {
      try {
        return await syncRef.current.promise;
      } finally {
        if (showLoading) {
          setLoading(false);
        }
      }
    }

    const syncPromise = loadUserFromToken(accessToken)
      .then((appUser) => {
        setUser(appUser);
        return appUser;
      })
      .catch((error) => {
        clearAuthState();
        throw error;
      });

    syncRef.current = { token: accessToken, promise: syncPromise };

    try {
      return await syncPromise;
    } finally {
      if (syncRef.current.token === accessToken) {
        syncRef.current = { token: accessToken, promise: null };
      }
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [clearAuthState, loadUserFromToken]);

  const axiosAuth = useCallback(() => {
    const currentToken = tokenRef.current;
    if (!currentToken) {
      throw new Error('Niet ingelogd');
    }

    return axios.create({
      baseURL: API_BASE_URL,
      headers: { Authorization: `Bearer ${currentToken}` },
    });
  }, []);

  const refreshUser = useCallback(async () => {
    const currentToken = tokenRef.current;
    if (!currentToken) return null;
    const appUser = await loadUserFromToken(currentToken);
    setUser(appUser);
    return appUser;
  }, [loadUserFromToken]);

  useEffect(() => {
    let mounted = true;

    if (!hasSupabaseConfig || !supabase) {
      clearAuthState();
      setLoading(false);
      return undefined;
    }

    const applySession = async (nextSession, options) => {
      if (!mounted) return;

      try {
        await syncSession(nextSession, options);
      } catch {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    const { data: listener } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!mounted || event === 'INITIAL_SESSION') {
        return;
      }

      if (event === 'TOKEN_REFRESHED') {
        if (nextSession?.access_token) {
          setSession(nextSession);
          tokenRef.current = nextSession.access_token;
        }
        return;
      }

      void applySession(nextSession);
    });

    void (async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        await applySession(data.session || null, { clearOnEmpty: false });
      } catch {
        if (mounted) {
          if (!syncRef.current.token) {
            clearAuthState();
          }
          setLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
      listener?.subscription?.unsubscribe();
    };
  }, [clearAuthState, syncSession]);

  const login = async (email, password) => {
    ensureSupabaseReady();
    const normalizedEmail = email.trim().toLowerCase();
    const { data, error } = await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password,
    });
    if (error) {
      throw new Error(error.message || 'Inloggen mislukt');
    }

    if (!data.session?.access_token) {
      throw new Error('Kon geen sessie starten');
    }

    const appUser = await syncSession(data.session);
    return { token: data.session.access_token, user: appUser };
  };

  const register = async (email, password, name) => {
    ensureSupabaseReady();
    const normalizedEmail = email.trim().toLowerCase();
    const normalizedName = name.trim();
    const { data, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        data: { name: normalizedName },
        emailRedirectTo: `${window.location.origin}/auth`,
      },
    });
    if (error) {
      const message = (error.message || '').toLowerCase();
      const isRateLimited =
        error.status === 429 ||
        message.includes('too many requests') ||
        message.includes('over_email_send_rate_limit') ||
        message.includes('security purposes');

      if (isRateLimited) {
        throw new Error('Te veel bevestigingsmails verstuurd. Probeer later opnieuw.');
      }

      throw new Error(error.message || 'Registratie mislukt');
    }

    if (!data.session?.access_token) {
      return {
        requiresEmailConfirmation: true,
        message: 'Check je mailbox om je account te bevestigen. Daarna kan je inloggen.',
      };
    }

    const appUser = await syncSession(data.session);
    return { token: data.session.access_token, user: appUser };
  };

  const requestPasswordReset = async (email) => {
    ensureSupabaseReady();

    const redirectTo = `${window.location.origin}/auth/recovery`;
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), { redirectTo });
    if (error) {
      throw new Error(error.message || 'Kon resetmail niet versturen');
    }
    return true;
  };

  const logout = () => {
    if (supabase) {
      supabase.auth.signOut().catch(() => {});
    }
    clearAuthState();
  };

  const updateUser = (nextUser) => {
    setUser(nextUser);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, requestPasswordReset, logout, axiosAuth, refreshUser, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
